// src/pages/ServiceFormPage.js
import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/ServiceFormPage.css';

const ServiceFormPage = () => {
  const [redirecting, setRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [serviceInfo, setServiceInfo] = useState({
    title: 'Start Your Business',
    type: 'Business',
    icon: 'building'
  });
  const { service } = useParams();
  const location = useLocation();
  const dubsadoFormUrl = "https://portal.dubsado.com/public/form/view/6697289e16c9b8003a062e6e";

  useEffect(() => {
    // Determine service type based on URL parameter or state passed from previous page
    if (service) {
      switch(service.toLowerCase()) {
        case 'llc':
          setServiceInfo({
            title: 'Start Your LLC Formation',
            type: 'LLC',
            icon: 'building'
          });
          break;
        case 'corporation':
          setServiceInfo({
            title: 'Start Your Corporation',
            type: 'Corporation',
            icon: 'landmark'
          });
          break;
        case 'compliance':
          setServiceInfo({
            title: 'Compliance Services',
            type: 'Compliance',
            icon: 'clipboard-check'
          });
          break;
        default:
          // Default service info is already set in useState
          break;
      }
    } else if (location.state && location.state.serviceType) {
      // Use service info passed via location state
      setServiceInfo(location.state.serviceType);
    }
  }, [service, location]);

  useEffect(() => {
    // Start countdown when component mounts
    if (redirecting) {
      if (countdown > 0) {
        const timer = setTimeout(() => {
          setCountdown(countdown - 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        // Redirect when countdown reaches 0
        window.location.href = dubsadoFormUrl;
      }
    }
  }, [redirecting, countdown, dubsadoFormUrl]);

  const handleContinue = () => {
    setRedirecting(true);
  };

  const handleDirectRedirect = () => {
    window.location.href = dubsadoFormUrl;
  };

  return (
    <div className="service-form-page">
      <SEO 
        title={`${serviceInfo.title} - Service Form`}
        description={`Get started with ${serviceInfo.title.toLowerCase()} services. Complete our form to begin your business formation process with Legate Consulting.`}
        keywords={`${serviceInfo.title.toLowerCase()}, business formation form, get started, business services, consulting form`}
        url={`https://legate-llp.com/service-form${service ? `/${service}` : ''}`}
      />
      <section className="service-form-header">
        <div className="container">
          <h1 className="service-form-title">{serviceInfo.title}</h1>
          <p className="service-form-subtitle">You're one step away from building your business on solid ground</p>
        </div>
      </section>

      <section className="section main-content">
        <div className="container">
          <div className="form-card">
            {redirecting ? (
              <div className="redirect-message">
                <div className="redirect-icon">
                  <i className="fas fa-sync fa-spin"></i>
                </div>
                <h2>You're being redirected to our secure form</h2>
                <p>We're taking you to our secure intake form in {countdown} seconds...</p>
                <div className="progress-bar">
                  <div 
                    className="progress" 
                    style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                  ></div>
                </div>
                <p className="small-text">
                  If you aren't redirected automatically, 
                  <button onClick={handleDirectRedirect} className="text-link">click here</button>
                </p>
              </div>
            ) : (
              <>
                <div className="intro-content">
                  <div className="intro-icon">
                    <i className={`fas fa-${serviceInfo.icon}`}></i>
                  </div>
                  <h2>We'll help you with your {serviceInfo.type} the right way</h2>
                  <p>
                    We're about to take you to our secure client intake form where you'll provide
                    the information we need to get your LLC formation started. Our process is:
                  </p>
                  <ul className="benefits-list">
                    <li>
                      <span className="checkmark"><i className="fas fa-check"></i></span>
                      Simple and user-friendly
                    </li>
                    <li>
                      <span className="checkmark"><i className="fas fa-check"></i></span>
                      Completely secure and confidential
                    </li>
                    <li>
                      <span className="checkmark"><i className="fas fa-check"></i></span>
                      Takes less than 10 minutes to complete
                    </li>
                    <li>
                      <span className="checkmark"><i className="fas fa-check"></i></span>
                      Followed up with personalized support
                    </li>
                  </ul>

                  <div className="action-buttons">
                    <button onClick={handleContinue} className="btn btn-primary btn-lg">
                      Continue to Secure Form
                    </button>
                    <Link to="/services" className="btn btn-outline">
                      Back to Services
                    </Link>
                  </div>
                </div>
                
                <div className="info-section">
                  <h3>What happens next?</h3>
                  <ol className="steps-list">
                    <li>
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <p>Complete the secure online intake form</p>
                      </div>
                    </li>
                    <li>
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <p>Our team reviews your information within 24 hours</p>
                      </div>
                    </li>
                    <li>
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <p>We prepare and file your {serviceInfo.type} documents</p>
                      </div>
                    </li>
                    <li>
                      <div className="step-number">4</div>
                      <div className="step-content">
                        <p>You receive confirmation and next steps</p>
                      </div>
                    </li>
                  </ol>
                </div>
              </>
            )}
          </div>
          
          <div className="trust-badges">
            <div className="trust-badge">
              <i className="fas fa-shield-alt"></i>
              <span>Secure & Confidential</span>
            </div>
            <div className="trust-badge">
              <i className="fas fa-check-circle"></i>
              <span>Expert Support</span>
            </div>
            <div className="trust-badge">
              <i className="fas fa-bolt"></i>
              <span>Fast Processing</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceFormPage;
